/**
 * API client
 * - 同源请求 (dev 时 Vite 代理 /api, prod 时同源)
 * - 自动带 device_token
 * - 401/403 触发登出回调
 * - 写操作 (POST/PATCH/DELETE) 网络失败时入队, 重连后重放
 */

const BASE = '';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let onUnauthorized: (() => void) | null = null;

/** 设置未授权回调 (鉴权 store 调用) */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export function getToken(): string | null {
  return localStorage.getItem('device_token');
}

/** 是不是网络错误 (fetch 抛 TypeError) */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

export async function api<T = unknown>(
  path: string,
  init?: RequestInit & { body?: string | undefined }
): Promise<T> {
  const token = getToken();
  // 没有 body 时不要发 Content-Type: application/json, 后端会因空 body 报错
  const hasBody = init?.body !== undefined && init.body !== null;
  const baseHeaders: Record<string, string> = {};
  if (hasBody) baseHeaders['Content-Type'] = 'application/json';
  if (token) baseHeaders['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(BASE + path, {
      ...init,
      headers: {
        ...baseHeaders,
        ...(init?.headers || {}),
      },
    });
  } catch (err) {
    // 网络错误, 写操作入队
    if (isNetworkError(err) && init?.method && init.method !== 'GET') {
      try {
        const body = init.body ? JSON.parse(init.body as string) : undefined;
        window.dispatchEvent(
          new CustomEvent('todo:write-offline', {
            detail: { method: init.method, path, body },
          })
        );
      } catch {
        /* ignore parse */
      }
    }
    throw err;
  }

  if (!res.ok) {
    // 401 / 403: 触发登出
    if (res.status === 401 || res.status === 403) {
      onUnauthorized?.();
    }
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText, body);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// 类型化 API 方法
import type {
  ListRow,
  TaskRow,
  StepRow,
  DeviceRow,
  PushSubscriptionInput,
} from './types';

export const listsApi = {
  list: () => api<ListRow[]>('/api/lists'),
  get: (id: string) => api<ListRow>(`/api/lists/${id}`),
  create: (input: { name: string; color?: string; icon?: string }) =>
    api<ListRow>('/api/lists', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<{ name: string; color: string; icon: string }>) =>
    api<ListRow>(`/api/lists/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string) => api<void>(`/api/lists/${id}`, { method: 'DELETE' }),
};

export const tasksApi = {
  list: (listId: string, opts?: { onlyCompleted?: boolean }) =>
    api<TaskRow[]>(
      `/api/tasks?list_id=${listId}${opts?.onlyCompleted ? '&only_completed=1' : ''}`
    ),
  listAll: () => api<TaskRow[]>('/api/tasks'),
  listMyDay: () => api<TaskRow[]>('/api/tasks/my-day'),
  listStarred: () => api<TaskRow[]>('/api/tasks/starred'),
  listArchived: () => api<TaskRow[]>('/api/tasks/archived'),
  archive: (id: string, version: number) =>
    api<TaskRow>(`/api/tasks/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),
  unarchive: (id: string, version: number) =>
    api<TaskRow>(`/api/tasks/${id}/unarchive`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),
  get: (id: string) => api<TaskRow>(`/api/tasks/${id}`),
  create: (input: {
    list_id: string;
    title: string;
    note?: string;
    due_date?: number;
    reminder_at?: number;
    importance?: number;
    starred?: number;
  }) => api<TaskRow>('/api/tasks', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, patch: Record<string, unknown>) =>
    api<TaskRow>(`/api/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string) => api<void>(`/api/tasks/${id}`, { method: 'DELETE' }),
  addToMyDay: (id: string) => api<TaskRow>(`/api/tasks/${id}/my-day`, { method: 'POST' }),
  removeFromMyDay: (id: string) =>
    api<TaskRow>(`/api/tasks/${id}/my-day`, { method: 'DELETE' }),
};

export const stepsApi = {
  list: (taskId: string) => api<StepRow[]>(`/api/tasks/${taskId}/steps`),
  create: (taskId: string, input: { title: string; sort_order?: number }) =>
    api<StepRow>(`/api/tasks/${taskId}/steps`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  update: (id: string, patch: Partial<{ title: string; completed: number }>) =>
    api<StepRow>(`/api/steps/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string) => api<void>(`/api/steps/${id}`, { method: 'DELETE' }),
};

export const devicesApi = {
  list: () => api<DeviceRow[]>('/api/devices'),
  rename: (id: string, name: string) =>
    api<DeviceRow>(`/api/devices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  block: (id: string, reason?: string) =>
    api<{ ok: boolean }>(`/api/devices/${id}/block`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  unblock: (id: string) =>
    api<{ ok: boolean }>(`/api/devices/${id}/unblock`, { method: 'POST' }),
  remove: (id: string) => api<void>(`/api/devices/${id}`, { method: 'DELETE' }),
};

export const authApi = {
  init: (password: string) =>
    api<{ device_id: string; device_token: string; device_name: string }>(
      '/api/auth/init',
      { method: 'POST', body: JSON.stringify({ password }) }
    ),
  check: () => api<{ ok: boolean; device: { id: string; name: string } }>('/api/auth/check'),
  vapidKey: () => api<{ publicKey: string }>('/api/push/vapid-public-key'),
};

export const pushApi = {
  subscribe: (sub: PushSubscriptionInput) =>
    api<{ id: number }>('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(sub),
    }),
  unsubscribeAll: () =>
    api<{ deleted: number }>('/api/push/subscribe', { method: 'DELETE' }),
};

export const syncApi = {
  snapshot: (since = 0) => api<{
    server_time: number;
    counts: { lists: number; tasks: number; steps: number };
    lists: ListRow[];
    tasks: TaskRow[];
    steps: StepRow[];
  }>(`/api/sync/snapshot?since=${since}`),
};